import { cloneDeep, isEmpty, merge } from "lodash";
import { CalendarMode, CalendarModeData, CalendarModeInitialData, CalendarView } from "PFApp/booking/types";
import {
  ProjectsResponse,
  useProjectsEntries
} from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { useProjectsInvalidate } from "PFCore/hooks/queries/bookings/projects/use_projects_invalidate";
import { MetaWithPagination } from "PFTypes";
import { useCallback, useEffect, useState } from "react";
import { useUnmount } from "react-use";

import {
  checkFiltersAreClear,
  checkFiltersAreDefault,
  getValueFilters
} from "../../../use_filtered_collection/format_filters";
import { BookingCalendarData } from "./context/booking_overview_context.types";
import { useCalendarView } from "./context/use_calendar_view";
import { DEFAULT_FILTERS_BY_VIEW, useProjectsFiltersOrder } from "./use_projects_filters";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_BY_VIEW = {
  [CalendarView.RolesAndAuditRoles]: DEFAULT_PAGE,
  [CalendarView.EngagementsAndAuditEngagements]: DEFAULT_PAGE
};
const DEFAULT_META = {} as unknown as MetaWithPagination;
const DEFAULT_PROJECTS_DATA = {
  data: { entries: [], meta: DEFAULT_META, loading: false },
  loaded: false,
  noFiltersSelected: true,
  noOrderSelected: true,
  onFilterChange: () => {},
  onFiltersChange: () => {},
  onFiltersReset: () => {},
  onOrderChange: () => {}
};

const PER_PAGE = 15;

interface UseProjectsData {
  enabled: boolean;
}

const useProjectsData = ({ enabled }: UseProjectsData): BookingCalendarData<{}, ProjectsResponse> => {
  const [view, setCalendarView] = useCalendarView(CalendarMode.Projects);

  const [pageByView, setPageByView] = useState<Partial<Record<CalendarView, number>>>(DEFAULT_PAGE_BY_VIEW);
  const page = pageByView[view] || 1;
  const setPage = useCallback(
    (pageNumber: number) => setPageByView((prev) => ({ ...prev, [view]: pageNumber })),
    [view]
  );
  const resetPagination = useCallback(() => setPage(DEFAULT_PAGE), [setPage]);

  const [projects, setProjects] = useState<CalendarModeData<ProjectsResponse>>(DEFAULT_PROJECTS_DATA);
  const projectsFiltersOrder = useProjectsFiltersOrder({ postChangeAction: resetPagination });
  const {
    filters: { selectedFilters, onFiltersChange, onFilterChange, onFiltersReset, onFiltersChangeRaw },
    order: { selectedOrder, setSelectedOrder, noOrderSelected }
  } = projectsFiltersOrder[view];

  const setView = useCallback(
    (view?: CalendarView, initialData?: Omit<CalendarModeInitialData, "view">) => {
      // in Projects tab we need to know the "view" to apply the initial filters correctly
      if (!view) {
        return;
      }
      setCalendarView(view);
      if (!initialData) {
        return;
      }
      const {
        filters: { onFiltersChange }
      } = projectsFiltersOrder[view];
      const { filters } = initialData;
      onFiltersChange(merge(cloneDeep(DEFAULT_FILTERS_BY_VIEW[view]), filters));
    },
    [projectsFiltersOrder, setCalendarView]
  );

  const { data, isLoading, isFetching } = useProjectsEntries(
    pageByView[view],
    view,
    PER_PAGE,
    selectedOrder,
    selectedFilters,
    {
      enabled: enabled
    }
  );

  const { removeCache: removeProjectsCache } = useProjectsInvalidate();
  useUnmount(removeProjectsCache);

  useEffect(() => {
    setProjects((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        loading: isLoading || isFetching
      }
    }));
  }, [isLoading, isFetching]);

  useEffect(() => {
    const basicFiltersAndOrderState = {
      onFilterChange,
      onFiltersChange,
      onFiltersReset,
      onOrderChange: setSelectedOrder,
      noOrderSelected
    };
    if (!data) {
      setProjects((prev) => ({ ...prev, ...basicFiltersAndOrderState }));
      return;
    }
    const { entries = [], meta = DEFAULT_META } = data;

    onFiltersChangeRaw(getValueFilters(meta.filters));

    setProjects((prev) => ({
      data: {
        ...prev.data,
        entries,
        meta,
        loading: false
      },
      loaded: true,
      noFiltersSelected:
        isEmpty(meta) ||
        checkFiltersAreClear(meta.filters, ["state"]) ||
        checkFiltersAreDefault(getValueFilters(meta.filters), DEFAULT_FILTERS_BY_VIEW[view]),
      ...basicFiltersAndOrderState
    }));
  }, [data]);

  return {
    ...projects,
    view,
    setView,
    page,
    setPage
  };
};

export default useProjectsData;
